<template>
  <!-- Empty div parent allows putting css on it without interfering with child css -->
  <div :class="{ 'position-relative': positionRelative }">
    <div v-if="shouldShow" class="popover" :class="[position]">
      <div class="popover-arrow" :class="[position]" />
      <div class="d-inline-block">
        <slot />
      </div>
      <button v-if="dismissible" class="popover-close" @click="dismiss()">&times;</button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue'

const props = withDefaults(
  defineProps<{
    id: string
    verticalPosition?: 'above' | 'below'
    horizontalPosition?: 'start' | 'end'
    dismissible?: boolean
    showUntil?: Date
    positionRelative?: boolean // set to false to position it more freely, e.g. to a relative parent
  }>(),
  {
    verticalPosition: 'above',
    horizontalPosition: 'start',
    dismissible: true,
    positionRelative: true,
  },
)

const emit = defineEmits(['close'])

const dismissed = ref(false)

const localStorageKey = `seen_${props.id}_msg`
const shouldShow = computed(() => !dismissed.value && !localStorage.getItem(localStorageKey) && (!props.showUntil || props.showUntil > new Date()))

const position = computed(() => `${props.verticalPosition || ''} ${props.horizontalPosition || ''}`)

const dismiss = () => {
  dismissed.value = true
  emit('close')
  localStorage.setItem(localStorageKey, '1')
}
</script>

<style lang="scss" scoped>
$backgroundColour: #888;
$borderColour: #555;
$offset: 25%;

.popover {
  z-index: 1;
  position: absolute;
  top: -6px;
  left: 50%;
  width: max-content;
  height: fit-content;
  padding: 0.5em;
  display: flex;

  font-size: 14px;
  font-weight: normal;

  text-shadow: none;
  color: white;
  background-color: $backgroundColour;
  border: 1px solid $borderColour;
  border-radius: 5px;

  &.above {
    transform: translate(-$offset, -100%);

    &.end {
      transform: translate(calc($offset - 100%), -100%);
    }
  }

  &.below {
    transform: translate(-$offset, 100%);

    &.end {
      transform: translate(calc($offset - 100%), 100%);
    }
  }

  .popover-arrow {
    $vPos: calc(-0.5em + 1px);

    position: absolute;
    width: 0.75em;
    height: 0.75em;

    background-color: $backgroundColour;
    border: 1px solid $borderColour;
    border-top: none;
    border-left: none;

    &.above {
      bottom: $vPos;
      left: $offset;
      transform: rotate(45deg);

      &.end {
        right: $offset;
        left: unset;
      }
    }

    &.below {
      top: $vPos;
      left: $offset;
      transform: rotate(225deg);

      &.end {
        right: $offset;
        left: unset;
      }
    }
  }

  .popover-close {
    color: white;
    border: none;
    background-color: transparent;
    margin: 0 0.1em 0 0.5em;

    font-size: 24px;
    line-height: 0;
    vertical-align: sub;
  }
}
</style>
